import toast from 'react-hot-toast';
import { LocalStorageInstance } from 'src/services/localStroage/LocalStorageService';
import { logger } from 'src/services/logger/loggerService';
import { checkIsPWOrg, getClientId, isWEBSDKSupported } from 'src/services/utils';
import { handleServerTime } from 'src/utility';

import { isTokenExpired } from './cookie-service';
import { isOTT } from './utilities';
import {
  RequestConfig,
  webSdkResponseDelete,
  webSdkResponseGet,
  webSdkResponsePatch,
  webSdkResponsePost,
  webSdkResponsePut,
} from './websdk-api-client';

export const getAccessToken = () => {
  const token = LocalStorageInstance.getToken();
  return token || '';
};

export const BASE_URL = process.env.REACT_APP_PP_API_BACKEND;
export const displayError = (message: any, shouldHide: any) => {
  !shouldHide && toast.error(JSON.stringify(message));
};
export const getRandomId = () => {
  const randomId = LocalStorageInstance.getRandomId();
  return randomId || '';
};

const createResponseObject = (response: Response, data: any = null) => ({
  status: response.status,
  data: data?.data || data,
  headers: response.headers,
  url: response.url,
});

export async function Client(endpoint: string, { body, ...customConfig } = {} as any) {
  const headers: any = {
    'Content-Type': 'application/json',
    version: '0.0.1',
    'client-id': getClientId(),
    'client-type': 'WEB',
    'client-version': 200,
    randomId: getRandomId(),
  };

  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    method: body ? 'POST' : 'GET',
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  };
  if (body) {
    if (config.headers['Content-Type'] === 'multipart/form-data') {
      delete config.headers['Content-Type'];
      config.body = body;
    } else {
      config.body = JSON.stringify(body);
    }
  }

  let data;
  const url = customConfig.endPointWithBaseUrl === true ? endpoint : BASE_URL + endpoint;

  if (customConfig?.withoutHeader || customConfig?.public) {
    delete config.headers;
  }

  try {
    const requestStartTime = Date.now();
    const response = await window.fetch(url, config);
    if (response.ok) {
      if (response.status === 204) {
        return createResponseObject(response);
      }

      // Check content type and length
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      logger.log('unexpected json error ----', 'contentLength', {
        contentLength,
        contentType,
        url: response.url,
      });

      // Handle empty or non-JSON responses
      if (contentLength === '0' || !contentType?.includes('application/json')) {
        logger.warn('Empty or non-JSON response received', 'contentLength');
        return createResponseObject(response);
      }

      // Check if response body is empty
      const responseClone = response.clone();
      let text = '';
      let shouldCheckText = true;

      try {
        text = await responseClone.text();
      } catch (textError) {
        logger.error('Failed to read response text:', textError);
        shouldCheckText = false;
        // Continue to try JSON parsing with original response
      }

      if (shouldCheckText && (!text || text.trim() === '')) {
        logger.warn('unexpected json', 'Empty response body received');
        return createResponseObject(response);
      }

      // Parse JSON safely
      try {
        data = await response.json();
        const serverTime = data?.data?.serverTime;
        if (serverTime) {
          handleServerTime(serverTime, requestStartTime);
        }
        return createResponseObject(response, data);
      } catch (jsonError) {
        logger.log(
          'unexpected json',
          'contentLength',
          'Failed to parse JSON:',
          jsonError,
          'Response text:',
          shouldCheckText ? text : 'Unable to read response text',
        );
        return createResponseObject(response, shouldCheckText ? text : null);
      }

      // return createResponseObject(response, data);
    } else if (response?.url?.includes('v1/videos/video-url-details')) {
      // Handle 403 for OTT videos - redirect to subscription
      if (response.status === 403) {
        if (isOTT()) {
          window.location.href = '/study-v2/pi/subscription';
          return null;
        }
      }
      return null;
    } else if (response.status === 401) {
      displayError('Un Authorized', customConfig?.shouldHide);
      // window.localStorage.clear();
      localStorage.removeItem('watchStats');

      if (checkIsPWOrg()) {
        const isTokenExired = isTokenExpired();
        if (!isTokenExired) {
          const responseData = await response.json();
          logger.error(
            { token },
            { endpoint },
            'token-expire-401-response',
            { isTokenExired },
            JSON.stringify(responseData),
          );
        }
        window.location.href = `${window.location.origin}/study-v2`;
      } else {
        window.location.href = '/';
      }
    } else if (response.status === 403) {
      data = await response.json();
      displayError(data.message || data?.error?.message || 'Forbidden', customConfig?.shouldHide);
      return Promise.reject(data.message || data?.error?.message || 'Forbidden');
    } else if (response.status === 412) {
      data = await response.json();
      displayError(
        data.message || data?.error?.message || 'Invalid grand',
        customConfig?.shouldHide,
      );
      return Promise.reject(data.message || data?.error || data?.error?.message || 'Invalid grand');
    } else if (response.status === 400 || response.status === 404) {
      data = await response.json();
      let message = data.message || data?.error;
      if (data.errorDetails) {
        message = data.errorDetails.map((e: any) => `${e.message || e.error}`).join(', ');
      }
      displayError(message?.message || message || 'Forbidden', customConfig?.shouldHide);
      return Promise.reject(message);
    } else if (response.status === 500 || response.status === 504 || response.status === 503) {
      data = await response.json();
      let message = data.message || data.error;
      if (data.errorDetails) {
        message = data.errorDetails.map((e: any) => `${e.message || e.error}`).join(', ');
      }
      displayError(message || 'Forbidden', customConfig?.shouldHide);
      return Promise.reject(message);
    }

    // const normalized = handleApiError({
    //   status: response.status,
    //   message: response.statusText,
    //   endpoint,
    // });
    // return Promise.reject(normalized.message);
    displayError(response.statusText || 'Something went wrong', customConfig?.shouldHide);
    return Promise.reject(response.statusText);
  } catch (err: any) {
    // const normalized = handleApiError({ status: err?.status, message: err?.message, endpoint });
    // return Promise.reject(normalized.message);
    return Promise.reject(err.message ? err.message : data);
  }
}

Client.get = async function (endpoint: string, customConfig: RequestConfig = {}) {
  if (isWEBSDKSupported()) {
    const resp = await webSdkResponseGet(endpoint, customConfig);
    return resp;
  }

  const params: any = [];
  if (customConfig.params) {
    for (const p in customConfig.params) {
      const key = p;
      if (Array.isArray(customConfig.params[p])) {
        customConfig.params[p].forEach((element: any) => {
          params.push(`${key}=${element}`);
        });
      } else {
        params.push(`${key}=${customConfig.params[p]}`);
      }
    }
  }

  const newEndpoints = customConfig?.params ? endpoint + '?' + params.join('&') : endpoint;
  return Client(newEndpoints, { ...customConfig, method: 'GET' });
};

Client.post = async function (endpoint: string, body: any, customConfig: RequestConfig = {}) {
  if (isWEBSDKSupported()) {
    const resp = await webSdkResponsePost(endpoint, body, customConfig);
    return resp;
  }

  const params: any = [];
  if (customConfig.params) {
    for (const p in customConfig.params) {
      const key = p;
      if (Array.isArray(customConfig.params[p])) {
        customConfig.params[p].forEach((element: any) => {
          params.push(`${key}=${element}`);
        });
      } else {
        params.push(`${key}=${customConfig.params[p]}`);
      }
    }
  }
  if (customConfig.params) {
    return Client(endpoint + '?' + params.join('&'), {
      body,
      ...customConfig,
      method: 'POST',
    });
  } else {
    delete customConfig.params;
    return Client(endpoint, { ...customConfig, body, method: 'POST' });
  }
};

Client.put = async function (endpoint: string, body: any, customConfig = {}) {
  if (isWEBSDKSupported()) {
    const resp = await webSdkResponsePut(endpoint, body, customConfig);
    return resp;
  }
  return Client(endpoint, { ...customConfig, body, method: 'PUT' });
};

Client.patch = async function (endpoint: string, body: any, customConfig = {}) {
  if (isWEBSDKSupported()) {
    const resp = await webSdkResponsePatch(endpoint, body, customConfig);
    return resp;
  }
  return Client(endpoint, { ...customConfig, body, method: 'PATCH' });
};

Client.delete = async function (endpoint: string, customConfig = {}) {
  if (isWEBSDKSupported()) {
    const resp = await webSdkResponseDelete(endpoint, customConfig);
    return resp;
  }
  return Client(endpoint, { ...customConfig, method: 'DELETE' });
};
